import { Injectable } from '@angular/core';
import { CanDeactivate } from '@angular/router';
import { Observable } from 'rxjs';

export interface ComponentWithUnsavedChanges {
  hasUnsavedChanges: () => boolean;
  bypassSubmit?: boolean;
  confirmationModal?: {
    openModalDialog: () => void;
    closeDialogModal: () => void;
  };
  leavePage?: () => void;
  stayOnPage?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class UnsavedChangesGuard implements CanDeactivate<ComponentWithUnsavedChanges> {
  //  Track active promise to prevent leaks
  private activePromise: Promise<boolean> | null = null;

  constructor() {
    //no code
  }

  // eslint-disable-next-line complexity
  canDeactivate(
    component: ComponentWithUnsavedChanges,
  ): Observable<boolean> | Promise<boolean> | boolean {
    console.log('🔍 Guard check:', {
      componentName: component.constructor.name,
      hasChanges: component.hasUnsavedChanges?.() ?? false,
      shouldBypass: component.bypassSubmit ?? false,
    });

    // 1.  Check bypass flag (form was submitted)
    if (this.shouldBypassNavigation(component)) {
      this.cleanup(component);
      return true;
    }

    // 2.  No unsaved changes → Allow navigation
    if (!this.hasUnsavedChanges(component)) {
      this.cleanup(component);
      return true;
    }

    // 3.  Has unsaved changes → Show confirmation
    return this.handleUnsavedChanges(component);
  }

  /**
   * Check if navigation should be bypassed
   */
  private shouldBypassNavigation(component: ComponentWithUnsavedChanges): boolean {
    if (component.bypassSubmit) {
      component.bypassSubmit = false;
      return true;
    }
    return false;
  }

  /**
   * Check if component has unsaved changes
   */
  private hasUnsavedChanges(component: ComponentWithUnsavedChanges): boolean {
    return component.hasUnsavedChanges?.() ?? false;
  }

  /**
   * Handle unsaved changes - show modal or browser confirm
   */
  private handleUnsavedChanges(component: ComponentWithUnsavedChanges): Promise<boolean> | boolean {
    //  If already showing modal, return existing promise
    if (this.activePromise) {
      return this.activePromise;
    }

    if (!this.hasModalImplementation(component)) {
      return confirm('You have unsaved changes. Do you really want to leave?');
    }

    return this.showConfirmationModal(component);
  }

  /**
   * Check if component has modal implementation
   */
  private hasModalImplementation(component: ComponentWithUnsavedChanges): boolean {
    return !!(component.confirmationModal && component.leavePage && component.stayOnPage);
  }

  /**
   * Show confirmation modal and return promise
   */
  private showConfirmationModal(component: ComponentWithUnsavedChanges): Promise<boolean> {
    //  Create and track promise
    this.activePromise = new Promise<boolean>((resolve) => {
      component.confirmationModal!.openModalDialog();

      //  Store original methods
      const originalLeavePage = component.leavePage!.bind(component);
      const originalStayOnPage = component.stayOnPage!.bind(component);

      //  Override leavePage
      component.leavePage = () => {
        component.confirmationModal!.closeDialogModal();

        //  Restore original methods
        component.leavePage = originalLeavePage;
        component.stayOnPage = originalStayOnPage;

        //  Clear active promise
        this.activePromise = null;

        //  Resolve promise
        resolve(true);

        //  Call original handler
        originalLeavePage();
      };

      //  Override stayOnPage
      component.stayOnPage = () => {
        component.confirmationModal!.closeDialogModal();

        //  Restore original methods
        component.leavePage = originalLeavePage;
        component.stayOnPage = originalStayOnPage;

        //  Clear active promise
        this.activePromise = null;

        //  Resolve promise
        resolve(false);

        //  Call original handler
        originalStayOnPage();
      };
    });

    return this.activePromise;
  }

  /**
   *  NEW: Clean up any stale state
   */
  private cleanup(component: ComponentWithUnsavedChanges): void {
    // Clear active promise
    this.activePromise = null;

    // Close modal if open
    if (component.confirmationModal) {
      try {
        component.confirmationModal.closeDialogModal();
      } catch (error) {
        // Modal might not be open, ignore error
      }
    }
  }
}
